import AlertBanner from 'core-system/AlertBanner'
import Banner from 'core-system/Banner'
import Loading from 'core-system/Loading'
import { ProgramMissingPaymentMethod } from 'core-system/Program'
import ProgramHeader from 'core-system/Program/ProgramHeader'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { qtfActions } from 'redux/qtf/qtfSlice'
import { useNavigate } from 'react-router-dom'
import { getClientEnvironment } from 'redux/config/services/Constants'
import { AppState } from 'redux/config/store'
import { QtfProgram, QtfProgramGroup } from 'redux/qtf/qtfTypes'
import useQueryParam from 'shared/Hooks/useQueryParam'
import QTFOverviewView from './QTFOverviewView'
import NoPaymentModal from 'shared/components/NoPaymentModal'

const QTFView = React.memo(() => {
  const segmentId = useQueryParam('segmentId')
  const navigate = useNavigate()

  const qtfPrograms = useSelector((state: AppState) => state.qtf.qtfPrograms)
  const {
    segmentsMap,
    hasValidPaymentMethod,
    hasValidDistributionType,
    profile,
  } = useSelector((state: AppState) => state.employer)
  const dispatch = useDispatch()
  const contractorCount = useSelector(
    (state: AppState) => state.qtf.dashboardStats?.contractorCount || 0
  )

  useEffect(() => {
    if (segmentId) {
      dispatch(qtfActions.getQTFDashboardStats({ segmentId }))
    }
  }, [dispatch, segmentId])

  const [programGroup, setProgramGroup] = useState<QtfProgramGroup>(null)
  const [isNoPaymentModalOpen, setIsNoPaymentModalOpen] = useState(false)
  const [showAlertBanner, setShowAlertBanner] = useState(
    contractorCount > 0
  )

  const onGetStartedClick = () => {
    if (hasValidPaymentMethod || !profile?.openRegistration) {
      navigate(`/tax-savings/${segmentId}/activation`)
    } else {
      setIsNoPaymentModalOpen(true)
    }
  }

  useEffect(() => {
    setProgramGroup({
      active: qtfPrograms[segmentId] && qtfPrograms[segmentId].active,
      nextMonth: qtfPrograms[segmentId] && qtfPrograms[segmentId].nextMonth,
    })
  }, [qtfPrograms, segmentId])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setShowAlertBanner(contractorCount > 0)
  }, [contractorCount])

  const segmentLocationLabel =
    segmentsMap[segmentId]?.type === 'CUSTOM' ? 'group' : 'worksite'

  if (!qtfPrograms || !programGroup) {
    return <Loading fullPage />
  }

  if (qtfPrograms && !programGroup.active && !programGroup.nextMonth) {
    return (
      <>
        {showAlertBanner && (
          <AlertBanner
            variant='yellowAlert'
            message={`${contractorCount} commuter${
              contractorCount === 1 ? '' : 's'
            } at this ${segmentLocationLabel} are contractors. They are not eligible for pre-tax benefits`}
            closeFn={() => setShowAlertBanner(false)}
            marginBottom='1rem'
          />
        )}
        <ProgramHeader segmentId={segmentId} isPreTax={true} />
        <Banner
          variant='cta'
          title='Start your Pre-Tax Commuter Program today!'
          description={`Give ${
            segmentsMap[segmentId].name === 'All Employees'
              ? 'All Commuters'
              : segmentsMap[segmentId].name
          } tax saving benefits today!`}
          cta='Get Started'
          ctaCallback={() => onGetStartedClick()}
          background='bannerGeneral3'
        />
        <NoPaymentModal
          open={isNoPaymentModalOpen}
          closeModal={() => setIsNoPaymentModalOpen(false)}
          product='qtf'
        />
      </>
    )
  }
  const isValid = hasValidDistributionType && hasValidPaymentMethod
  const isNextMonth = !programGroup.active && programGroup.nextMonth
  const isCancelling =
    !programGroup.active &&
    programGroup.nextMonth &&
    programGroup.nextMonth.typeMap?.COMMUTER_TRANSIT?.status === 'CANCELLING' &&
    programGroup.nextMonth.typeMap?.COMMUTER_PARKING?.status === 'CANCELLING'
  const isPending = isNextMonth && !isCancelling
  const isDevCheck =
    getClientEnvironment() === 'development' && !isPending && !isCancelling
  const isOverviewCheck = programGroup.active && isValid

  return (
    <>
      {showAlertBanner && (
        <AlertBanner
          variant='yellowAlert'
          message={`${contractorCount} commuters at this ${segmentLocationLabel} are contractors. They are not eligible for pre-tax benefits`}
          closeFn={() => setShowAlertBanner(false)}
          marginBottom='1rem'
        />
      )}
      <ProgramHeader
        segmentId={segmentId}
        manageRoute={
          isDevCheck || isOverviewCheck
            ? `/tax-savings/${segmentId}/manage`
            : null
        }
        isPreTax={true}
      />
      {isPending && (
        <Banner
          variant='cta'
          background='bannerGeneral3'
          title='Starting Soon!'
          description={`Commuters will be able to start allocating funds on ${moment(
            programGroup.nextMonth.startDate
          ).format(
            'MMMM Do, YYYY'
          )} and they will be able to spend these funds starting on ${moment(
            programGroup.nextMonth.hrInitialDisplayStartDate
          ).format('MMMM Do, YYYY')}. `}
          cta='Manage Program'
          ctaCallback={() => navigate(`/tax-savings/${segmentId}/manage`)}
        />
      )}
      {isCancelling && (
        <Banner
          variant='cta'
          background='bannerGeneral3'
          title='Program Cancelling'
          description='Your program cancellation request has been submitted. Our team will reach out soon.'
          cta='Manage Program'
          ctaCallback={() => navigate(`/tax-savings/${segmentId}/manage`)}
        />
      )}
      {isDevCheck ? (
        <QTFOverviewView activeProgram={programGroup.active} />
      ) : (
        <>
          {!isValid && (
            <ProgramMissingPaymentMethod from={`/tax-savings/${segmentId}`} />
          )}
          {isOverviewCheck && (
            <QTFOverviewView
              activeProgram={programGroup.active as QtfProgram}
            />
          )}
        </>
      )}
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  QTFView.displayName = 'QTFView'
}

export default QTFView
